import { createBobAssetResources, loadBobAsset } from './148-bob-art.mjs';

/** Share one rigid original crate asset across a course's spatial windows.
 * attach receives only the fallback shell mesh; its parent keeps kind markings,
 * canonical coordinates and consumed visibility. Return a detach callback for
 * each retiring window. The exported 1.1-unit shell fits the existing unit box.
 * Failure preserves every shell; disposal releases shared GPU data exactly once. */
export function createBobCrateArt({
  THREE,
  prepareMaterial = (material) => material,
  onInvalidate = () => {},
  onStatus = () => {},
  loadModel = loadBobAsset,
  url = '/assets/bob-bounce/02-crate.glb',
}) {
  const abort = new AbortController();
  const resources = createBobAssetResources();
  const attachments = new Set();
  let disposed = false,
    template = null,
    status = 'loading';
  function report(value) {
    status = value;
    onStatus(value);
  }
  function mount(entry) {
    if (!template || disposed || entry.model || !entry.shell.parent) return;
    const model = template.clone(true);
    model.name = 'Original Tripo crate shell';
    model.scale.multiplyScalar(1 / 1.1);
    entry.shell.parent.add(model);
    entry.model = model;
    entry.shell.visible = false;
  }
  function detach(entry) {
    entry.model?.removeFromParent();
    entry.shell.visible = entry.visible;
    attachments.delete(entry);
  }
  report('loading');
  const ready = Promise.resolve()
    .then(() =>
      loadModel({
        THREE,
        url,
        signal: abort.signal,
        own: resources.own,
      }),
    )
    .then((asset) => {
      resources.collect(asset?.scene);
      if (disposed) return false;
      let meshes = 0,
        skinned = false;
      const prepared = new Set();
      asset?.scene?.traverse((node) => {
        if (!node.isMesh) return;
        meshes++;
        skinned ||= node.isSkinnedMesh;
        node.castShadow = node.receiveShadow = true;
        for (const material of Array.isArray(node.material) ? node.material : [node.material]) {
          if (!prepared.has(material)) {
            prepared.add(material);
            prepareMaterial(material);
          }
        }
      });
      if (!meshes || skinned) throw new Error('Bob crate must contain rigid mesh geometry.');
      template = asset.scene;
      for (const entry of attachments) mount(entry);
      report('ready');
      onInvalidate();
      return true;
    })
    .catch(() => {
      template = null;
      for (const entry of [...attachments]) detach(entry);
      resources.dispose();
      if (!disposed) {
        report('fallback');
        onInvalidate();
      }
      return false;
    });
  return {
    ready,
    get status() {
      return status;
    },
    attach(shell) {
      if (disposed) return () => {};
      const entry = { shell, visible: shell.visible, model: null };
      attachments.add(entry);
      mount(entry);
      return () => detach(entry);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      status = 'disposed';
      abort.abort();
      for (const entry of [...attachments]) detach(entry);
      template = null;
      resources.dispose();
    },
  };
}
